/** @jsxImportSource @emotion/react */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import cloneDeep from 'lodash/cloneDeep';
import { tb } from 'components/layouts/Table';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { FileTypeName, PageMode } from 'models/common/Common';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { MenuContextType } from 'models/admin/Menu';
import { MenuContext } from 'App';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  SetupDailyReport,
  SetupDailyReportCondition,
  SetupPeople,
} from 'models/setup/SetupDailyReport';
import useEvent from 'react-use-event-hook';
import dayjs from 'dayjs';
import { CommonUtil } from 'utils/CommonUtil';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import ScheduleStatusDetailContent from 'pages/setup/schedule/ScheduleStatusDetailContent';
import SetupDailyReportIssueContent from './SetupDailyReportIssueContent';
import {
  findLatestDailyReport,
  saveDailyReport,
  generateId,
  findDailyReportPeople,
  findDailyReportInfo,
  copyDailyReport,
  findDailyReportStartDt,
  findDailyReportList,
} from 'apis/setup/SetupDailyReport';
import CustomTextarea from 'components/inputs/CustomTextarea';
import CustomEditorInputWithSearch from 'components/inputs/CustomEditorInputWithSearch';
import CrossEditor from 'components/crosseditor/CrossEditor';
import _ from 'lodash';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import useSessionStore from 'stores/useSessionStore';
import { Folding } from 'components/buttons/Folding';
import SetupDailyReportEquipmentContent from './SetupDailyReportEquipmentContent';
import SetupDailyReportCalendarButton from './SetupDailyReportCalendarButton';
import Dropzone from 'components/dropzone/Dropzone';
import FileGrid from 'components/dropzone/FileGrid';
import { FileSaveResponse, FileSaveResult } from 'models/admin/FileInfo';
import { FindInputBtnSet } from 'components/layouts/TableInButton';
import { checkAvailableProjectManager, findProjectById } from 'apis/setup/SetupProject';
import { EVENT_TYPES, EventBus } from 'utils/EventUtil';
import { hasRole } from 'utils/SessionUtil';
import { reloadScheduleResult } from 'apis/setup/SetupSchedule';
import ProjectIssueCreatePage from '../issue/ProjectIssueRegisterPage';

interface PageLocationState {
  mode?: PageMode;
  elmPrjId?: string;
  excpId?: string;
  excpNm?: string;
  crtnDt?: string;
  reportList?: any;
}

/**
 * @desc 프로젝트 셋업 일보 조회 > 셋업일보 등록/수정 화면
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.11.11   jd             신규 생성
 * 2025.11.27   jshwang12      작업일보에서 사용하는 엑셀템플릿다운로드/업로드 버튼 위치 이동 (-> 일정상세현황 그리드 상단으로 위치 이동)
 * 2026.01.14   jshwang12      신규등록인 경우에도 이슈목록 조회/등록 가능 (기준일 변경 시 이슈목록 자동조회), 일보 시작일 조회 분리
 * 2026.01.16   jshwang12      설비 작업자 수 변경 시 변경된 합계 적용되도록 수정, 용어변경 (특이사항 / FA 요청 사항 -> 협력업체 Comment, 협력업체 요청사항 -> LGES Comment)
 * 2026.01.20   jshwang12      작업자 수 소수점 2자리까지 입력 가능 (관리자, 설비별 작업자 모두), 작업자 컬럼 변경
 * 2026.01.26   jshwang12      수정, 저장 등 버튼 상단 추가 (일정상세현황 그리드 높이 늘려서 스크롤 없애면서 상하단 모두 추가)
 * 2026.01.27   jshwang12      일정현황상세 > 실적불러오기 기능 추가
 * 2026.02.24   jihwan89       <> 버튼 추가: 버튼으로 일보 이동 1) 버튼 노출 : 등록완료, 2) 임시저장상태는 버튼으로 이동 X
 * 2026.03.04   jihwan89       <> 버튼 처음 조회한 협력업체의 일보만 이동 조회되도록 수정
 * 2026.04.14   jshwang12      일정현황상세 > 현황 더블클릭 시 해당 설비와 단계를 기본값으로 하는 이슈등록팝업을 노출, 이슈등록 버튼 프로젝트 담당자도 이슈둥록 가능하도록 권한 추가
 * 2026.07.08   jihwan89       셋업일보 처음 작성시 처음 입력(현황 0) 하면 일보작성일이 실제 시작일로 들어가도록 수정
 * 2026.09.17   jshwang12      <> 버튼, 일보이동달력 수정: 임시저장 상태인 경우 작성자에 한해 노출/이동 O, 수정상태인 경우에만 미노출
 */
const SetupDailyReportRegistPage = () => {
  const { t } = useTranslation();
  const menuContext = useContext<MenuContextType>(MenuContext);
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { userId, empNm, ensolYn, deptNm } = useSessionStore();
  const [errors, setErrors] = useState<any>({});
  const dropzoneRef = useRef<any>();

  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const location = useLocation();
  const state = { ...location.state };
  const navigate = useNavigate();

  const defaultCondition = {
    elmPrjId: '',
    excpId: '',
    crtnDt: '',
    wkId: '',
  };

  const [editMode, setEditMode] = useState<string>(locationState?.mode ?? 'READ');
  const [condition, setCondition] = useState<SetupDailyReportCondition>(defaultCondition);

  const [prevWkId, setPrevWkId] = useState<string>('');
  const [wkId, setWkId] = useState<string>('');

  const [wkerDivsCd, setWkerDivsCd] = useState<Code[]>([]);
  const [wktDtlDivsCd, setWktDtlDivsCd] = useState<Code[]>([]);

  const [searchParams] = useSearchParams();

  const [prjInfo, setPrjInfo] = useState<SetupDailyReport>();
  // const [originPrjInfo, setOriginPrjInfo] = useState<SetupDailyReport>();

  const [wkInfo, setWkInfo] = useState<SetupDailyReport>();
  const [crtnStDt, setCrtnStDt] = useState<string>();
  const [peopleInfo, setPeopleInfo] = useState<SetupPeople>();
  const [reportList, setReportList] = useState<any>([]);

  const [baseInfo, setBaseInfo] = useState<SetupDailyReport>();

  const tabStatdRef = useRef<any>();
  const issueRef = useRef<any>();
  const equipRef = useRef<any>();

  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [isExcelValid, setExcelValid] = useState<boolean>(false);
  const [isOpenFolding, setOpenFolding] = useState<boolean>(true);
  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);
  const [issuePopupCondition, setIssuePopupCondition] = useState<any>();

  const [hasModifyAuth, setHasModifyAuth] = useState<boolean>(false); /** 협력사 수정권한 */
  const [hasModifyMgrAuth, setHasModifyMgrAuth] = useState<boolean>(false); /** 담당자 수정권한 */

  const isCopying = useRef<boolean>(false);

  const peopleSummary = useMemo(() => {
    if (!peopleInfo) return null;
    const hqSum =
      Number(peopleInfo.hqPm ?? 0) +
      Number(peopleInfo.hqMach ?? 0) +
      Number(peopleInfo.hqCtrl ?? 0) +
      Number(peopleInfo.hqElpw ?? 0) +
      Number(peopleInfo.hqSafy ?? 0) +
      Number(peopleInfo.hqTrsn ?? 0);
    const wkerSum =
      Number(peopleInfo.ctrlCnt ?? 0) +
      Number(peopleInfo.machCnt ?? 0) +
      Number(peopleInfo.trspCnt ?? 0) +
      Number(peopleInfo.elpwCnt ?? 0);
    /*const wkerSum =
      Number(peopleInfo.wkerCtrl ?? 0) +
      Number(peopleInfo.wkerElpw ?? 0) +
      Number(peopleInfo.wkerLocl ?? 0) +
      Number(peopleInfo.wkerMach ?? 0) +
      Number(peopleInfo.wkerSafy ?? 0);*/

    const totalSum = hqSum + wkerSum;
    const korSum = totalSum - Number(peopleInfo.wkerLocl ?? 0);
    const locSum = Number(peopleInfo.wkerLocl ?? 0);
    return {
      ...peopleInfo,
      hqSum: CommonUtil.defaultZeroNumberTrunc(hqSum),
      wkerSum: CommonUtil.defaultZeroNumberTrunc(wkerSum),
      totalSum: CommonUtil.defaultZeroNumberTrunc(totalSum),
      korSum: CommonUtil.defaultZeroNumberTrunc(korSum),
      locSum: CommonUtil.defaultZeroNumberTrunc(locSum),
    };
  }, [peopleInfo]);

  const completedStat = useMemo(() => {
    return wkInfo?.wkStatCd === 'REG_CPLT';
  }, [wkInfo]);

  const savedStat = useMemo(() => {
    return wkInfo?.wkStatCd === 'SAVE';
  }, [wkInfo]);

  const showNavButtons = useMemo(() => {
    // 달력, 이전글, 다음글 버튼 노출조건 변경
    if (editMode !== PageMode.READ || !wkInfo?.wkStatCd) return false;
    if (savedStat) return wkInfo?.dataInsUserId === userId;
    return true;
  }, [editMode, savedStat, wkInfo, userId]);

  const isEditable = useMemo(() => {
    // 신규 / 수정모드일 경우
    if (editMode === PageMode.WRITE || editMode === PageMode.EDIT || editMode === PageMode.COPY) {
      return true;
    } else {
      return false;
    }
  }, [condition.elmPrjId, editMode]);

  useEffect(() => {
    //공통코드 호출
    getCommonCodes();

    // read 모드로 최초 세팅
    if (editMode !== PageMode.WRITE) {
      setEditMode(PageMode.READ);
    }
  }, []);

  useEffect(() => {
    getStatusContent(wkId);
    getEquipContent(wkId);
  }, [wkId]);

  useEffect(() => {
    getStatusContent(prevWkId);
    getEquipContent(prevWkId);
  }, [prevWkId]);

  const getCommonCodes = async () => {
    const wkerDivsCd = await getCommonCodeNamesCondition({
      cmnGrCd: 'WKER_DIVS_CD',
    });
    setWkerDivsCd(wkerDivsCd);

    const WktDtlDivsCd = await getCommonCodeNamesCondition({
      cmnGrCd: 'WKT_DTL_DIVS_CD',
    });
    setWktDtlDivsCd(WktDtlDivsCd);
  };

  const getStatusContent = async (id?: string) => {
    tabStatdRef.current?.search({
      elmPrjId: condition.elmPrjId,
      excpId: condition.excpId,
      wkId: id,
    });
  };

  const getEquipContent = async (id?: string) => {
    equipRef.current?.search({
      elmPrjId: condition.elmPrjId,
      excpId: condition.excpId,
      wkId: id,
    });
  };

  const getIssueContent = async () => {
    issueRef.current?.search({
      elmPrjId: condition.elmPrjId,
      excpId: condition.excpId,
      crtnDt: condition.crtnDt,
    });
  };

  useEffect(() => {
    if (state?.elmPrjId) {
      if (locationState) {
        // 목록에서 다른 항목을 선택한 경우 (신규, 수정 탭이 모두 열려있는 경우 구분하기 위해 조건 추가)
        setLocationState((prev) => (prev?.mode === state?.mode ? state : prev));
      } else {
        setLocationState(state);
      }
    }
  }, [location]);

  useEffect(() => {
    if (!_.isEmpty(locationState?.elmPrjId)) {
      setCondition((prev) => ({
        ...prev,
        elmPrjId: locationState.elmPrjId || searchParams.get('elmPrjId') || '',
        excpId: locationState.excpId || searchParams.get('excpId') || '',
        crtnDt: locationState.crtnDt || searchParams.get('crtnDt') || '',
      }));
      setReportList(locationState.reportList || []);
    }
  }, [locationState]);

  useEffect(() => {
    (async () => {
      let auth = false;
      if (ensolYn === 'N') {
        await checkAvailableProjectManager(condition?.elmPrjId ?? '', 'EXCP').then(
          (res) => (auth = res)
        );
      }
      setHasModifyAuth(auth);
    })();
  }, [condition?.elmPrjId, editMode]);

  useEffect(() => {
    (async () => {
      let auth = false;
      if (ensolYn === 'Y') {
        // Y인 경우에만 체크
        if (hasRole('FA_MANAGER')) {
          auth = true;
        } else {
          await checkAvailableProjectManager(condition?.elmPrjId ?? '', 'HQCOP').then(
            (res) => (auth = res)
          );
        }
      }
      setHasModifyMgrAuth(auth);
    })();
  }, [condition?.elmPrjId, editMode]);

  useEffect(() => {
    if (condition.elmPrjId && condition.excpId) {
      (async () => {
        if (editMode === PageMode.WRITE && _.isEmpty(wkId)) {
          setWkId(await generateId(condition?.elmPrjId ?? '', condition?.excpId ?? ''));
        }

        // 작업일보 기본정보 조회 (복사할때 조회하므로 여기에서 조회X)
        if (editMode !== PageMode.COPY) {
          await loadBaseInfo(
            condition?.elmPrjId ?? '',
            condition?.excpId ?? '',
            condition.crtnDt ?? ''
          );
          // [26.01.14] 신규 등록인 경우에도 이슈 목록 조회/등록 가능 (기준일 변경 시 이슈목록 자동조회)
          getIssueContent();
        }
        // [26.01.14] 일보 작업 시작일 조회
        findDailyReportStartDt(condition?.elmPrjId ?? '', condition?.excpId ?? '').then((result) =>
          setCrtnStDt(result)
        );
      })();
    }
  }, [condition.elmPrjId, condition.excpId, condition.crtnDt, editMode]);

  useEffect(() => {
    // 상세조회
    if (wkId && condition?.excpId) {
      if (editMode !== PageMode.WRITE && editMode !== PageMode.COPY) {
        findDailyReportPeople(wkId, condition?.excpId ?? '').then((result) => {
          setPeopleInfo(result);
        });
      }

      // getIssueContent();
    }
  }, [wkId, condition?.excpId, editMode]);

  const loadBaseInfo = useCallback(
    async (elmPrjId: string, excpId: string, crtnDt?: string) => {
      let dt = '';
      if (editMode === PageMode.WRITE || editMode === PageMode.COPY) {
        dt = isCopying.current ? crtnDt ?? '' : '';
      } else {
        dt = crtnDt ?? '';
      }

      await Promise.all([
        findProjectById(elmPrjId),
        findDailyReportInfo(elmPrjId, excpId, dt),
      ]).then(([prj, wk]) => {
        if (prj) {
          const prjInfo = cloneDeep(prj);
          setPrjInfo(prjInfo);
        }
        if (wk) {
          let wkInfo = cloneDeep(wk[0]);
          if (editMode !== PageMode.WRITE && editMode !== PageMode.COPY) {
            setWkId(wkInfo?.wkId ?? '');
            if (wkInfo?.wkId) {
              // 설비 조회
              getEquipContent(wkInfo?.wkId);
              // 일정현황상세 조회
              getStatusContent(wkInfo?.wkId);
            }
          } else {
            if (!isCopying.current) {
              wkInfo = {
                ...wkInfo,
                wkProgCtn: '',
                nextPlanCtn: '',
                sngrMatrCtn: '',
                excpRelsCtn: '',
                atchFileGrId: '',
              };
            }
          }
          setWkInfo(wkInfo);
        }
      });
    },
    [editMode]
  );

  const downloadExcelTemplate = useCallback(async () => {
    // [26.02.02] 실적불러오기, 템플릿다운로드 기준일자 선택 후 클릭 가능으로 변경
    if (!condition.crtnDt) {
      openCommonModal({
        modalType: 'alert',
        content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
      });
      /*openMessageBar({
        type: 'error',
        content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
      });*/
      return false;
    }
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_DRPT_LIST,
      condition?.elmPrjId ?? '',
      [],
      condition?.excpId,
      wkId,
      condition?.crtnDt
    ).finally(() => openLoading(false));
  }, [wkId, condition?.crtnDt]);

  const handleWkInfoChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setWkInfo({
      ...wkInfo,
      [name]: value || '',
    });

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  // 금일작업내용/명일작업내용/협력업체 Comment 에디터
  const wkProgEditorRef = useRef<CrossEditor>(null);
  const nextPlanEditorRef = useRef<CrossEditor>(null);
  const sngrMatrEditorRef = useRef<CrossEditor>(null);

  const handleEditorOnLoad = (ref: React.RefObject<CrossEditor>, value?: string) => {
    if (!ref.current?.editorRef) return;
    ref.current?.editorRef.SetBodyValue(CommonUtil.safeHtmlContents(value ?? '', false));
  };

  const handleEditorChange = (e: any, ref: React.RefObject<CrossEditor>) => {
    const target = e?.editorName;
    const targetText = e?.editorTarget?.GetBodyValue();
    setWkInfo((prev) => ({ ...prev, [target]: targetText || '' }));
    setErrors((prev) => ({ ...prev, [target]: false }));
    ref.current?.editorRef.SaveBodyValue?.();
  };

  const handlePeopleInfoChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    setPeopleInfo({
      ...peopleInfo,
      [name]: value || '',
    });

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const saveDummy = useCallback(async () => {
    if (wkId) {
      const saveRowsData = {
        wkId: wkId,
        crtnDt: condition.crtnDt,
        excpId: condition.excpId,
        editMode: editMode,
      };

      await saveDailyReport({
        ...saveRowsData,
      }).then((result) => {
        console.log(result);
      });
    }
  }, [wkId, condition.crtnDt, condition.excpId]);

  const btnCopy = useEvent(
    useCallback(async () => {
      if (_.isEmpty(condition?.crtnDt)) {
        openCommonModal({
          modalType: 'alert',
          content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
        });
        /*openMessageBar({
          type: 'error',
          content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
        });*/
        return;
      }

      const latest = await findLatestDailyReport(
        condition?.elmPrjId ?? '',
        condition?.excpId ?? '',
        condition?.crtnDt ?? ''
      );

      if (!latest?.wkId) {
        openCommonModal({
          modalType: 'alert',
          content: t('setup.label.복사할 데이터가 없습니다.', '복사할 데이터가 없습니다.'),
        });
        /*openMessageBar({
          type: 'error',
          content: t('setup.label.복사할 데이터가 없습니다.', '복사할 데이터가 없습니다.'),
        });*/
        return;
      }

      openCommonModal({
        modalType: 'confirm',
        content: t(
          'setup.label.해당 일자에 등록된 데이터는 삭제 후 최신 일자로 복사합니다.<br/>복사 하시겠습니까?',
          '해당 일자에 등록된 데이터는 삭제 후 최신 일자로 복사합니다.<br/>복사 하시겠습니까?'
        ),
        yesCallback: async () => {
          // isCopying.current = true;
          // 기존 일보 데이터 초기화 (일보정보, 작업자, 설비, 실적이력) 후 복사
          copyDailyReport(wkId, latest?.wkId, condition?.crtnDt)
            .then(async (res) => {
              if (res?.successOrNot === 'Y') {
                const result = res?.data;
                if (!result || !result?.wkId) {
                  openCommonModal({
                    modalType: 'alert',
                    content: String(
                      t('setup.label.복사 중 오류가 발생했습니다.', '복사 중 오류가 발생했습니다.')
                    ),
                  });
                  /*openMessageBar({
                    type: 'error',
                    content: String(
                      t('setup.label.복사 중 오류가 발생했습니다.', '복사 중 오류가 발생했습니다.')
                    ),
                  });*/
                  return;
                }
                setWkId(result?.wkId);
                setEditMode(PageMode.EDIT);
                // setPrevWkId(latest?.wkId);
                setCondition({
                  ...condition,
                  elmPrjId: result?.elmPrjId,
                  excpId: result?.excpId,
                  crtnDt: result?.crtnDt,
                });

                await loadBaseInfo(
                  result?.elmPrjId ?? '',
                  result?.excpId ?? '',
                  result.crtnDt ?? ''
                );

                await findDailyReportPeople(result?.wkId ?? '', result?.excpId ?? '').then(
                  (people) => {
                    setPeopleInfo(people);
                  }
                );

                getIssueContent();
                getStatusContent(result?.wkId ?? '');
                getEquipContent(result?.wkId ?? '');

                openMessageBar({
                  type: 'confirm',
                  content: t(
                    `setup.label.${latest.crtnDt} 일자 일보를 불러왔습니다.`,
                    `${latest.crtnDt} 일자 일보를 불러왔습니다.`
                  ),
                });
              } else {
                openCommonModal({
                  modalType: 'alert',
                  content:
                    res.data && typeof res.data === 'string'
                      ? res.data
                      : t(
                          'setup.label.복사 중 오류가 발생했습니다',
                          '복사 중 오류가 발생했습니다.'
                        ),
                });
                /*openMessageBar({
                  type: 'error',
                  content:
                    res.data && typeof res.data === 'string'
                      ? res.data
                      : t(
                          'setup.label.복사 중 오류가 발생했습니다',
                          '복사 중 오류가 발생했습니다.'
                        ),
                });*/
              }
            })
            .finally(() => {
              // isCopying.current = false;
            });
        },
      });
    }, [condition?.elmPrjId, condition?.excpId, condition?.crtnDt, editMode, wkId])
  );

  const btnSave = useEvent(
    useCallback(
      async (mode: 'DRAFT' | 'SAVE' | 'DELETE' | 'COPY') => {
        if (mode !== 'DELETE' && !condition?.crtnDt) {
          openCommonModal({
            modalType: 'alert',
            content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
          });
          /*openMessageBar({
            type: 'error',
            content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
          });*/

          return;
        }

        let isValid = true;
        await findDailyReportInfo(
          condition?.elmPrjId ?? '',
          condition?.excpId ?? '',
          condition?.crtnDt ?? ''
        ).then((result) => {
          if (editMode === PageMode.WRITE || editMode === PageMode.COPY) {
            if ((result || []).length > 0 && editMode === PageMode.WRITE) {
              openCommonModal({
                modalType: 'alert',
                content: t(
                  'setup.label.해당 날짜에는 작성된 일보가 있습니다.',
                  '해당 날짜에는 작성된 일보가 있습니다.'
                ),
              });
              /*openMessageBar({
                type: 'error',
                content: t(
                  'setup.label.해당 날짜에는 작성된 일보가 있습니다.',
                  '해당 날짜에는 작성된 일보가 있습니다.'
                ),
              });*/
              isValid = false;
            }
          } else {
            if ((result || []).length < 1) {
              openCommonModal({
                modalType: 'alert',
                content: t(
                  'setup.label.해당 날짜에는 작성된 일보가 없습니다.\n 일보 조회 후 시도해주세요.',
                  '해당 날짜에는 작성된 일보가 없습니다.\n 일보 조회 후 시도해주세요.'
                ),
              });
              /*openMessageBar({
                type: 'error',
                content: t(
                  'setup.label.해당 날짜에는 작성된 일보가 없습니다.\n 일보 조회 후 시도해주세요.',
                  '해당 날짜에는 작성된 일보가 없습니다.\n 일보 조회 후 시도해주세요.'
                ),
              });*/
              isValid = false;
            }
          }
          if (mode !== 'DELETE') {
            /*if (
              wkInfo?.wkProgCtn &&
              new TextEncoder().encode(wkInfo?.wkProgCtn || '').length > 4000
            ) {
              openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.금일작업내용은 최대 4,000 Bytes까지 입력 가능합니다.',
                  '금일작업내용은 최대 4,000 Bytes까지 입력 가능합니다.'
                ),
              });
              isValid = false;
            } else if (
              wkInfo?.nextPlanCtn &&
              new TextEncoder().encode(wkInfo?.nextPlanCtn || '').length > 4000
            ) {
              openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.명일작업내용은 최대 4,000 Bytes까지 입력 가능합니다.',
                  '명일작업내용은 최대 4,000 Bytes까지 입력 가능합니다.'
                ),
              });
              isValid = false;
            } else if (
              wkInfo?.sngrMatrCtn &&
              new TextEncoder().encode(wkInfo?.sngrMatrCtn || '').length > 1000
            ) {
              openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.협력업체 Comment는 최대 1,000 Bytes까지 입력 가능합니다.',
                  '협력업체 Comment는 최대 1,000 Bytes까지 입력 가능합니다.'
                ),
              });
              isValid = false;
            } else if (
              wkInfo?.excpRelsCtn &&
              new TextEncoder().encode(wkInfo?.excpRelsCtn || '').length > 4000
            ) {
              openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.LGES Comment는 최대 4,000 Bytes까지 입력 가능합니다.',
                  'LGES Comment는 최대 4,000 Bytes까지 입력 가능합니다.'
                ),
              });
              isValid = false;
            }*/
          }
        });
        if (!isValid) {
          return;
        }

        // 파일 저장은 협력사만
        let msg = t('setup.label.저장하시겠습니까?', '저장하시겠습니까?');
        let atchFileGrId = '';
        if (ensolYn === 'N') {
          const fileSaveResponse: FileSaveResponse = await dropzoneRef.current.saveFiles();
          if (fileSaveResponse.fileSaveResult !== FileSaveResult.FAIL) {
            atchFileGrId = fileSaveResponse.atchFileGrId;
          }
        }

        const saveRowsData = {
          ...prjInfo,
          ...wkInfo,
          wkId: wkId,
          prevWkId: prevWkId,
          excpId: condition.excpId,
          elmPrjId: condition.elmPrjId,
          crtnDt: CommonUtil.toYmd(condition.crtnDt || '') || condition.crtnDt,
          peopleInfo: peopleInfo,
          saveType: mode,
          editMode: mode === 'DELETE' ? PageMode.DELETE : editMode,
          workEquipment: equipRef.current?.getRowData(),
          ...(ensolYn === 'N' && { atchFileGrId: atchFileGrId }),
        };

        if (mode === 'SAVE') {
          //console.log('wk hCnt ---->', Number(wkInfo?.hCnt ?? '0'));
          // if (!isExcelValid) {
          //   msg = t(
          //     'setup.label.실적 업로드 하지 않았습니다. 작업 일보만 저장하시겠습니까?',
          //     '실적 업로드 하지 않았습니다. 작업 일보만 저장하시겠습니까?'
          //   );
          // }
        } else if (mode === 'DELETE') {
          msg = t('setup.label.해당 일보를 삭제 하시겠습니까?', '해당 일보를 삭제 하시겠습니까?');
        }

        openCommonModal({
          modalType: 'confirm',
          content: msg,
          yesCallback: async () => {
            if (mode !== 'DELETE') {
              await tabStatdRef.current?.save();
            }
            await saveDailyReport({
              ...saveRowsData,
            }).then((result) => {
              if (result == null || result?.successOrNot === 'N') {
                const failText =
                  result?.data && typeof result?.data === 'string'
                    ? result?.data
                    : t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.');
                openCommonModal({
                  modalType: 'alert',
                  content: failText,
                });
                return;
              } else {
                openMessageBar({
                  type: 'confirm',
                  content:
                    mode !== 'DELETE'
                      ? t('com.label.저장되었습니다.', '저장되었습니다.')
                      : t('com.label.초기화되었습니다.', '초기화되었습니다.'),
                });

                // loadBaseInfo(
                //   condition?.elmPrjId ?? '',
                //   condition?.excpId ?? '',
                //   condition?.crtnDt ?? ''
                // );

                // getStatusContent(wkId);
                // getEquipContent(wkId);
                EventBus.publish(EVENT_TYPES.SETUP_DAILY_LIST);
                menuContext.closeCurrentTab(menuContext);
              }
            });
          },
        });
      },
      [wkId, condition.crtnDt, wkInfo, peopleInfo, isExcelValid]
    )
  );

  const handleDate = (value) => {
    if (!value) return;
    if (!value && completedStat) return;
    const formatted = value ? dayjs(String(value)).format('YYYYMMDD') : '';

    if (!condition?.elmPrjId || !condition?.excpId) {
      /*openMessageBar({
        type: 'error',
        content: t('setup.label.잘못된 접근입니다.', '잘못된 접근입니다.'),
      });*/
      openCommonModal({
        modalType: 'alert',
        content: t('setup.label.잘못된 접근입니다.', '잘못된 접근입니다.'),
      });
      return;
    }

    if (formatted) {
      openLoading(true);
      setCondition((prev) => ({
        ...prev,
        crtnDt: value,
      }));

      findDailyReportInfo(condition.elmPrjId, condition.excpId, formatted)
        .then((result) => {
          const crtnDt = formatted;
          if (editMode === PageMode.WRITE || editMode === PageMode.COPY) {
            // 일보 신규등록화면인 경우 작성된 일보있는 경우 선택 불가
            if ((result || []).length > 0) {
              openCommonModal({
                modalType: 'alert',
                content: t(
                  'setup.label.작성된 일보가 있습니다.\n다른 일자를 선택해주세요.',
                  '작성된 일보가 있습니다.\n다른 일자를 선택해주세요.'
                ),
              });
              /*openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.작성된 일보가 있습니다.\n다른 일자를 선택해주세요.',
                  '작성된 일보가 있습니다.\n다른 일자를 선택해주세요.'
                ),
              });*/
            }
          } else {
            // 일보 상세화면인 경우 작성된 일보없는 경우 선택 불가
            if ((result || []).length < 1) {
              openCommonModal({
                modalType: 'alert',
                content: t('setup.label.작성된 일보가 없습니다.', '작성된 일보가 없습니다.'),
              });
              /*openMessageBar({
                type: 'warning',
                content: t('setup.label.작성된 일보가 없습니다.', '작성된 일보가 없습니다.'),
              });*/
              const dt = baseInfo?.crtnDt ? baseInfo?.crtnDt : '';
              setCondition((prev) => ({
                ...prev,
                crtnDt: dt,
              }));
              return;
            }
          }
          setCondition((prev) => ({
            ...prev,
            crtnDt: crtnDt,
          }));
          setBaseInfo((prev) => ({
            ...prev,
            crtnDt: crtnDt,
          }));

          // [26.09.09] 달력으로 기존 목록(reportList)에 없던 날짜로 이동한 경우에도
          // <> 화살표 이동이 정상 동작하도록 현재 협력사 기준 목록을 다시 조회해 둔다.
          findDailyReportList({
            elmPrjId: condition.elmPrjId,
            excpId: condition.excpId,
            dailyReportYn: 'Y',
          }).then((list) => {
            // crtnDt를 항상 'YYYYMMDD' 8자리로 맞춰야 handleOnNext의 매칭(===)이 정상 동작한다.
            const normalized = (list || []).map((item) => ({
              ...item,
              crtnDt: String(item.crtnDt || '').replace(/[^0-9]/g, ''),
            }));
            setReportList(
              normalized.sort((a, b) => (b.crtnDt || '').localeCompare(a.crtnDt || ''))
            );
          });
        })
        .finally(() => openLoading(false));
    }
    // else {
    //   setCondition((prev) => ({
    //     ...prev,
    //     crtnDt: formatted,
    //   }));
    //   setBaseInfo((prev) => ({
    //     ...prev,
    //     crtnDt: formatted,
    //   }));
    // }
  };

  const btnCancel = useCallback(async () => {
    // 상세모드로 전환
    // if (editMode !== PageMode.WRITE) {
    //   getStatusContent(wkId);
    //   getEquipContent(wkId);
    // }
    if (editMode === PageMode.WRITE) {
      menuContext.closeCurrentTab(menuContext);
    } else {
      setEditMode(PageMode.READ);
    }
  }, []);

  const btnModify = () => {
    // 수정모드로 전환
    setEditMode(PageMode.EDIT);
  };

  const handleDownloadExcel = useCallback(() => {
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.DAILY_REPORT,
      wkInfo?.wkId ?? '',
      [],
      condition.elmPrjId ?? '',
      condition.excpId ?? '',
      condition.crtnDt ?? ''
    ).finally(() => openLoading(false));
  }, [wkInfo?.wkId, condition.elmPrjId, condition.excpId, condition.crtnDt]);

  const renderBtn = (
    id: 'DRAFT' | 'SAVE' | 'DELETE' | 'CANCEL' | 'COPY' | 'EDIT',
    className: string,
    text?: string
  ) =>
    useMemo(() => {
      if (id === 'SAVE') {
        const isVisible = isEditable || editMode === PageMode.COPY;
        if (!isVisible) {
          return;
        }
      } else if (id === 'DRAFT') {
        const isVisible =
          editMode !== PageMode.READ &&
          (editMode === PageMode.WRITE || editMode === PageMode.COPY || savedStat);
        if (!isVisible) {
          return;
        }
      } else if (id === 'DELETE') {
        const isVisible = editMode !== PageMode.READ && savedStat;
        if (!isVisible) {
          return;
        }
      } else if (id === 'CANCEL') {
        // 취소버튼 : editMode -> edit
        const isVisible = editMode !== PageMode.READ;
        if (!isVisible) {
          return;
        }
      } else if (id === 'EDIT') {
        // 수정버튼 : editMode -> Read
        const isVisible = editMode === PageMode.READ;
        if (!isVisible) {
          return;
        }
        if (ensolYn === 'N' && !hasModifyAuth) {
          return;
        }
        if (ensolYn === 'Y' && !hasModifyMgrAuth) {
          return;
        }
      } else if (id === 'COPY') {
        // ( 등록완료)로 와서 수정하는 경우 복사 버튼 숨김
        // 임시저장인 경우는 복사 버튼 보여 줌
        if ((editMode !== PageMode.WRITE && completedStat) || editMode === PageMode.READ) {
          return;
        }
        if (!hasModifyAuth) {
          return;
        }
      }

      const dispatch: (action: string) => void = (id: string) => {
        switch (id) {
          case 'DRAFT':
            return btnSave(id);
          case 'SAVE':
            return btnSave(id);
          case 'CANCEL':
            return btnCancel();
          case 'DELETE':
            return btnSave(id);
          case 'COPY':
            return btnCopy();
          case 'EDIT':
            return btnModify();
        }
      };

      const btn = (
        <Button
          css={IconButton.button}
          className={className}
          disableRipple
          onClick={() => dispatch(id)}
        >
          {text ? text : ''}
        </Button>
      );
      return <>{btn}</>;
    }, [wkInfo, editMode]);

  const diffDays = (startDt?: string, endDt?: string) => {
    return dayjs(endDt).diff(dayjs(startDt), 'day');
  };

  const allWorkDays = useMemo(() => {
    if (condition?.crtnDt) {
      const workDay = diffDays(crtnStDt || condition?.crtnDt, condition?.crtnDt) + 1;
      //return (workDay - Number(wkInfo?.optValCtn1 || 0)).toLocaleString();
      return workDay.toLocaleString();
    }
    return '';
  }, [condition?.crtnDt, crtnStDt, wkInfo?.optValCtn1]);

  const onInputWorker = (e) => {
    const target = e.target as HTMLInputElement;
    let value = target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      while (value.length > 0 && !regex.test(value)) {
        value = value.slice(0, -1);
      }
    }
    const result = Number(value);
    if (!Number.isNaN(result)) {
      target.value = result ? (Math.trunc(result * 100) / 100).toLocaleString() : '';
    }
    target.value = value;
  };

  // 26.02.24 화살표 버튼으로 일보 이동 추가
  const handleOnNext = (param) => {
    const masterData = {
      excpId: condition?.excpId,
      crtnDt: condition?.crtnDt,
    };

    // 첫 조회한 협력업체만 조회
    const filteredExcpId = reportList.filter((o) => o.excpId === condition.excpId);
    // [26.09.17] 임시저장 일보는 작성자 본인 것만 이동 대상에 포함
    const notSaveList = filteredExcpId.filter(
      (o) => o.wkStatCd !== 'SAVE' || o.dataInsUserId === userId
    );
    // 현재 idx
    const itemIdx = notSaveList?.findIndex((o) =>
      Object.keys(masterData).every((key) => o[key] === masterData[key])
    );
    // next 인지 prev 인지
    const idx = param == 'next' ? itemIdx - 1 : itemIdx + 1;

    if (param === 'next' && idx < 0) {
      // idx = notSaveList.length - 1;
      return;
    } else if (param === 'prev' && idx === notSaveList.length) {
      // idx = 0;
      return;
    }

    const elmPrjId = notSaveList?.find((o, i) => i == idx).elmPrjId || '';
    const excpId = notSaveList?.find((o, i) => i == idx).excpId || '';
    const crtnDt = notSaveList?.find((o, i) => i == idx).crtnDt || '';

    const searchPageCondition = {
      elmPrjId: elmPrjId,
      excpId: excpId,
      crtnDt: crtnDt,
      wkId: '',
      reportList: notSaveList,
    };

    setCondition(searchPageCondition);
  };

  /**
   * 단계 이슈 등록 팝업 열기
   * [26.04.14] 일정현황상세 > 현황 클릭 시 해당 설비와 단계를 기본값으로 하는 이슈등록팝업을 노출
   */
  const onOpenCreateIssuePopup = useEvent((prjSpCd, tgtEqpId, eqpId, eqpNm, eqclId) => {
    // 수정버튼 노출 조건과 동일
    const isVisible = editMode === PageMode.READ;
    if (!isVisible) {
      return;
    }
    if (ensolYn === 'N' && !hasModifyAuth) {
      return;
    }
    if (ensolYn === 'Y' && !hasModifyMgrAuth) {
      return;
    }
    if (prjSpCd && tgtEqpId && eqpId && eqpNm && eqclId) {
      setIssuePopupCondition({
        prjSpCd: prjSpCd,
        tgtEqpId: tgtEqpId,
        eqpId: eqpId,
        eqpNm: eqpNm,
        eqclId: eqclId,
      });
      setOpenIssuePopup(true);
    }
  });

  return (
    <>
      <style>
        {`
          .base-table {
            table-layout: fixed;
            width: 100%;
          }
          .text-truncate {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: inherit;
          }
          .report-table .v-middle {
            vertical-align: middle !important;
          }
          .report-table .col-index {
            width: 140px;
            max-width: 170px;
            // vertical-align: middle !important;
          }
           
          .report-table .col-name {
             width: 160px;
             // font-weight: 500;
          }
          
          .report-table .col-index2 {
            width: 200px;
            // text-align: center;
          }
          
          .report-table .col-name2 {
            width: 160px;
            // font-weight: 500;
            vertical-align: center !important;
          }
          
          .report-table .col-index3 {
            width: 60px;
            // text-align: center;
          }
          
          .report-table .col-name3 {
            width: 200px;
            // font-weight: 500;
          }
          
          .report-table .col-index4 {
            width: 140px;
            // text-align: center;
          }
          
          .report-table .col-name4 {
            min-width: 160px;
            // font-weight: 500;
          }
          
          .report-table .col-index5 {
            width: 60px;
            // text-align: center;
           }
          
          .report-table .col-name5 {
            min-width: 80px;
            // font-weight: 500;
          }
          .report-read-content {
            height: fit-content;
            // max-height: 510px;
            overflow-y: auto;
            overflow-x: hidden;
            padding-right: 8px;
            padding-top: 4px;
            padding-bottom: 4px;
            white-space: pre-wrap;
            word-break: break-word;
          }
          
          .hidden-id {
            color: transparent;
            user-select: text;
          }
          
          .hidden-id::selection {
            color: #333;
            background: #ffeeba;
          }
          
          .hint::before {
            content: "ID ";
            color: #bbb;
          }
        `}
      </style>
      <div css={Folding.fold(isOpenFolding)}>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('setup.label.셋업일보 정보', '셋업일보 정보')}</h3>
            <div className="hidden-id">ID : {wkId}</div>
          </SubTitleGroup>
          <GlobalBtnGroup>
            {showNavButtons ? (
              <>
                <SetupDailyReportCalendarButton
                  elmPrjId={condition?.elmPrjId}
                  excpId={condition?.excpId}
                  value={condition?.crtnDt}
                  onSelectDate={(dateStr) => handleDate(dateStr)}
                />
                <Button
                  css={IconButton.button}
                  className="left iconOnly"
                  disableRipple
                  onClick={() => handleOnNext('prev')}
                />
                <Button
                  css={IconButton.button}
                  className="right iconOnly"
                  disableRipple
                  onClick={() => handleOnNext('next')}
                />
              </>
            ) : (
              ''
            )}
            {editMode !== PageMode.WRITE ? (
              <Button
                css={IconButton.button}
                className="icoNone"
                onClick={handleDownloadExcel}
                disableRipple
              >
                {t('com.button.Excel 다운로드', 'Excel 다운로드')}
              </Button>
            ) : (
              ''
            )}
            {renderBtn('CANCEL', 'cancel', t('com.button.취소', '취소') ?? '')}
            {renderBtn('EDIT', 'edit', t('com.button.수정', '수정') ?? '')}
            {renderBtn('DELETE', 'delete', t('com.button.삭제', '삭제') ?? '')}
            {renderBtn('DRAFT', 'draft', t('com.button.임시저장', '임시저장') ?? '')}
            {renderBtn('SAVE', 'save', t('com.button.저장', '저장') ?? '')}
          </GlobalBtnGroup>
        </SubTitleLayout>
        <TableContainer css={tb.table}>
          <Table className="base-table">
            <colgroup>
              <col className="col-index" />
              <col className="col-name" />
              <col className="col-index2" />
              <col className="col-name2" />
              <col className="col-index3" />
              <col className="col-name3" />
              <col className="col-index4" />
              <col className="col-name4" />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.플랜트', '플랜트')}</span>
                </TableCell>
                <TableCell className={'tool-cell'}>{prjInfo?.bildPlntCdNm}</TableCell>
                <TableCell>
                  <span>{t('setup.label.구분', '구분')}</span>
                </TableCell>
                <TableCell>{prjInfo?.prjDivsCdNm}</TableCell>
                <TableCell>
                  <span>{t('setup.label.프로젝트 ID', '프로젝트 ID')}</span>
                </TableCell>
                <TableCell>{prjInfo?.elmPrjId}</TableCell>
                <TableCell>
                  <span>{t('setup.label.프로젝트 기간', '프로젝트 기간')}</span>
                </TableCell>
                <TableCell>
                  {CommonUtil.formatPeriod(prjInfo?.ivesStDt ?? '', prjInfo?.ivesEndDt ?? '')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.프로젝트 명', '프로젝트 명')}</span>
                </TableCell>
                <TableCell colSpan={7}>{prjInfo?.elmPrjNm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.협력업체명', '협력업체명')}</span>
                </TableCell>
                <TableCell className={'tool-cell'}>{locationState?.excpNm || deptNm}</TableCell>
                <TableCell>
                  <span className={isEditable ? 'dot' : ''}>
                    {t('setup.label.기준일자', '기준일자')}
                  </span>
                </TableCell>
                {/*(임시저장, 등록완료) 기존 작업 일보 데이터를 조회해서 수정 하는 경우 --> 기준일자 Read Only*/}
                <TableCell>
                  <FindInputBtnSet>
                    <CustomDatepicker
                      isRange={false}
                      value={condition?.crtnDt}
                      minDate={null}
                      maxDate={new Date()}
                      onChange={handleDate}
                      readOnly={editMode !== PageMode.WRITE && (completedStat || savedStat)}
                      disabled={editMode !== PageMode.WRITE && (completedStat || savedStat)}
                    />
                    {renderBtn('COPY', 'copyRow')}
                    {/*<Button css={IconButton.button} className="copyRow" onClick={btnCopy} />*/}
                  </FindInputBtnSet>
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.작성자', '작성자')}</span>
                </TableCell>
                <TableCell>{wkId === wkInfo?.wkId ? wkInfo?.dataInsUserNm : empNm}</TableCell>
                <TableCell>
                  <span>{t('setup.label.작성일시', '작성일시')}</span>
                </TableCell>
                <TableCell>
                  {wkId === wkInfo?.wkId
                    ? wkInfo?.dataInsDtm
                      ? dayjs(wkInfo?.dataInsDtm).format('YYYY.MM.DD')
                      : ''
                    : ''}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('setup.label.작업 일수/작업 인원', '작업 일수/작업 인원')}</h3>
          </SubTitleGroup>
        </SubTitleLayout>
        <TableContainer css={tb.table} style={{ marginBottom: '24px' }}>
          <Table className="report-table">
            <colgroup>
              <col className="col-index" />
              <col className="col-name" />
              <col className="col-index2" />
              <col className="col-name2" />
              <col className="col-index3" />
              <col className="col-name3" />
              <col className="col-index4" />
              <col className="col-name4" />
              <col className="col-index5" />
              <col className="col-name5" />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.시작일', '시작일')}</span>
                </TableCell>
                <TableCell>
                  {(crtnStDt || condition?.crtnDt) &&
                    CommonUtil.formatPeriod(crtnStDt || condition?.crtnDt, '')}
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.작업일', '작업일')}</span>
                </TableCell>
                <TableCell>
                  {condition?.crtnDt &&
                    `${(
                      diffDays(crtnStDt || condition?.crtnDt, condition?.crtnDt) +
                      1 -
                      Number(wkInfo?.optValCtn1 || 0)
                    ).toLocaleString()}${t('setup.label.일', '일')}`}
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.작업 제외일', '작업 제외일')}</span>
                </TableCell>
                <TableCell>
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="optValCtn1"
                      name="optValCtn1"
                      value={wkInfo?.optValCtn1}
                      placeholder={String(t('setup.labe.작업 제외일', '작업 제외일'))}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.value = target.value.replace(/[^0-9]/g, '');
                      }}
                      onChange={(e) => {
                        setWkInfo((prev) => ({ ...prev, optValCtn1: e.target.value }));
                      }}
                    />
                  ) : (
                    <span>
                      {CommonUtil.defaultZeroNumberCellTemplate(wkInfo?.optValCtn1)}
                      {t('setup.label.일', '일')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.전체 작업일', '전체 작업일')}</span>
                </TableCell>
                <TableCell>{allWorkDays && `${allWorkDays}${t('setup.label.일', '일')}`}</TableCell>
                <TableCell>
                  <span>{t('setup.label.전체 진행율', '전체 진행율')}</span>
                </TableCell>
                <TableCell>
                  {(Math.trunc(Number(wkInfo?.prcdRt ?? '0') * 10) / 10).toLocaleString()}
                </TableCell>
              </TableRow>
              {/*<TableRow>*/}
              {/*  <TableCell>*/}
              {/*    <span>{t('setup.label.금일', '금일')}</span>*/}
              {/*  </TableCell>*/}
              {/*  <TableCell>시스템 자동</TableCell>*/}
              {/*  <TableCell>*/}
              {/*    <span>{t('setup.label.본사', '본사')}</span>*/}
              {/*  </TableCell>*/}
              {/*  <TableCell>시스템 자동</TableCell>*/}
              {/*  <TableCell>*/}
              {/*    <span>{t('setup.label.작업자', '작업자')}</span>*/}
              {/*  </TableCell>*/}
              {/*  <TableCell>시스템 자동</TableCell>*/}
              {/*  <TableCell>*/}
              {/*    <span>{t('setup.label.한국인', '한국인')}</span>*/}
              {/*  </TableCell>*/}
              {/*  <TableCell></TableCell>*/}
              {/*  <TableCell>*/}
              {/*    <span>{t('setup.label.현지인', '현지인')}</span>*/}
              {/*  </TableCell>*/}
              {/*  <TableCell></TableCell>*/}
              {/*</TableRow>*/}
              <TableRow>
                <TableCell className="row odd v-middle" rowSpan={4}>
                  <span>{t('setup.label.관리자', '관리자')}</span>
                </TableCell>
                <TableCell className="odd">
                  <span>{t('setup.label.PM', 'PM')}</span>
                </TableCell>
                <TableCell className="noBg">
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqPm"
                      name="hqPm"
                      value={peopleInfo?.hqPm}
                      placeholder={String(t('setup.msg.PM 인원수', 'PM 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqPm)}</span>
                  )}
                </TableCell>
                {/*<TableCell rowSpan={4}>본사 합계 (시스템 자동)</TableCell>*/}
                {/*<TableCell className="row odd" rowSpan={4}>*/}
                {/*  <span>{t('setup.label.작업자', '작업자')}</span>*/}
                {/*</TableCell>*/}
                <TableCell className="odd">
                  <span>{t('setup.label.통역', '통역')}</span>
                </TableCell>
                <TableCell className="noBg">
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqTrsn"
                      name="hqTrsn"
                      value={peopleInfo?.hqTrsn}
                      placeholder={String(t('setup.msg.통역 인원수', '통역 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqTrsn)}</span>
                  )}
                </TableCell>
                <TableCell className="row odd" rowSpan={4}>
                  <span>{t('setup.label.작업자', '작업자')}</span>
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.운송', '운송')}</span>
                </TableCell>
                <TableCell>
                  {CommonUtil.defaultZeroNumberTrunc(peopleInfo?.trspCnt)}
                  {/*{isEditable ? (*/}
                  {/*  <CustomInputWithSearch*/}
                  {/*    id="wkerSafy"*/}
                  {/*    name="wkerSafy"*/}
                  {/*    value={peopleInfo?.wkerSafy}*/}
                  {/*    placeholder={String(t('setup.msg.안전관리자 인원수', '안전관리자 인원수'))}*/}
                  {/*    onInput={(e) => {*/}
                  {/*      const target = e.target as HTMLInputElement;*/}
                  {/*      target.value = target.value.replace(/[^0-9]/g, '');*/}
                  {/*    }}*/}
                  {/*    onChange={(e) => handlePeopleInfoChange(e)}*/}
                  {/*  />*/}
                  {/*) : (*/}
                  {/*  <span>{CommonUtil.defaultZeroNumberCellTemplate(peopleInfo?.wkerSafy)}</span>*/}
                  {/*)}*/}
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.전체인원', '전체인원')}</span>
                </TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleSummary?.totalSum)}</TableCell>
                {/*<TableCell rowSpan={4}>본사 합계 (시스템 자동)</TableCell>*/}
                {/*<TableCell rowSpan={4} colSpan={2}></TableCell>*/}
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.기구', '기구')}</span>
                </TableCell>
                <TableCell>
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqMach"
                      name="hqMach"
                      value={peopleInfo?.hqMach}
                      placeholder={String(t('setup.msg.기구 인원수', '기구 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqMach)}</span>
                  )}
                </TableCell>
                <TableCell>
                  <span>{t('setup.label.안전관리자', '안전관리자')}</span>
                </TableCell>
                <TableCell>
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqSafy"
                      name="hqSafy"
                      value={peopleInfo?.hqSafy}
                      placeholder={String(t('setup.msg.안전관리자 인원수', '안전관리자 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqSafy)}</span>
                  )}
                </TableCell>
                <TableCell>{t('setup.label.기구', '기구')}</TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.machCnt)}</TableCell>
                <TableCell>
                  <span>{t('setup.label.관리자(합계)', '관리자(합계)')}</span>
                </TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleSummary?.hqSum)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.전장', '전장')}</span>
                </TableCell>
                <TableCell>
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqElpw"
                      name="hqElpw"
                      value={peopleInfo?.hqElpw}
                      placeholder={String(t('setup.msg.본사 인원수', '본사 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqElpw)}</span>
                  )}
                </TableCell>
                <TableCell>{/*<span>{t('setup.label.제어', '제어')}</span>*/}</TableCell>
                <TableCell>
                  {/*{isEditable ? (
                    <CustomInputWithSearch
                      id="wkerCtrl"
                      name="wkerCtrl"
                      value={peopleInfo?.wkerCtrl}
                      placeholder={String(t('setup.msg.작업자 인원수', '작업자 인원수'))}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.value = target.value.replace(/[^0-9]/g, '');
                      }}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberCellTemplate(peopleInfo?.wkerCtrl)}</span>
                  )}*/}
                </TableCell>
                <TableCell>{t('setup.label.전장', '전장')}</TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.elpwCnt)}</TableCell>
                <TableCell>
                  <span>{t('setup.label.작업자(합계)', '작업자(합계)')}</span>
                </TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleSummary?.wkerSum)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('setup.label.제어', '제어')}</span>
                </TableCell>
                <TableCell>
                  {isEditable && hasModifyAuth ? (
                    <CustomInputWithSearch
                      id="hqCtrl"
                      name="hqCtrl"
                      value={peopleInfo?.hqCtrl}
                      placeholder={String(t('setup.msg.제어 인원수', '제어 인원수'))}
                      onInput={onInputWorker}
                      onChange={(e) => handlePeopleInfoChange(e)}
                    />
                  ) : (
                    <span>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hqCtrl)}</span>
                  )}
                </TableCell>
                <TableCell>{/*<span>{t('setup.label.현지인', '현지인')}</span>*/}</TableCell>
                <TableCell>
                  {/*{isEditable ? (*/}
                  {/*  <CustomInputWithSearch*/}
                  {/*    id="wkerLocl"*/}
                  {/*    name="wkerLocl"*/}
                  {/*    value={peopleInfo?.wkerLocl}*/}
                  {/*    placeholder={String(t('setup.msg.현지인 인원수', '현지인 인원수'))}*/}
                  {/*    onInput={(e) => {*/}
                  {/*      const target = e.target as HTMLInputElement;*/}
                  {/*      target.value = target.value.replace(/[^0-9]/g, '');*/}
                  {/*    }}*/}
                  {/*    onChange={(e) => handlePeopleInfoChange(e)}*/}
                  {/*  />*/}
                  {/*) : (*/}
                  {/*  <span>{CommonUtil.defaultZeroNumberCellTemplate(peopleInfo?.wkerLocl)}</span>*/}
                  {/*)}*/}
                </TableCell>
                <TableCell>{t('setup.label.제어', '제어')}</TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.ctrlCnt)}</TableCell>
                <TableCell>
                  <span>{t('setup.label.중장비(합계)', '중장비(합계)')}</span>
                </TableCell>
                <TableCell>{CommonUtil.defaultZeroNumberTrunc(peopleInfo?.hepCnt)}</TableCell>
                {/*<TableCell>*/}
                {/*  <span>{t('setup.label.안전관리자', '안전관리자')}</span>*/}
                {/*</TableCell>*/}
                {/*<TableCell>*/}
                {/*  {isEditable ? (*/}
                {/*    <CustomInputWithSearch*/}
                {/*      id="wkerSafy"*/}
                {/*      name="wkerSafy"*/}
                {/*      value={peopleInfo.wkerSafy}*/}
                {/*      placeholder={String(t('setup.msg.안전관리자 인원수', '안전관리자 인원수'))}*/}
                {/*      onInput={(e) => {*/}
                {/*        const target = e.target as HTMLInputElement;*/}
                {/*        target.value = target.value.replace(/[^0-9]/g, '');*/}
                {/*      }}*/}
                {/*      onChange={(e) => handlePeopleInfoChange(e)}*/}
                {/*    />*/}
                {/*  ) : (*/}
                {/*    <span>{CommonUtil.defaultZeroNumberCellTemplate(peopleInfo.wkerSafy)}</span>*/}
                {/*  )}*/}
                {/*</TableCell>*/}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* 이슈 목록 */}
      <SetupDailyReportIssueContent
        ref={issueRef}
        readOnly={
          !isEditable ||
          (ensolYn === 'N' && !hasModifyAuth) ||
          (ensolYn === 'Y' && !hasModifyMgrAuth) // [26.04.14] 일정현황상세 > 현황 클릭 시 이슈등록 팝업과 동일한 권한 (담당자 이슈둥록 가능)
        }
        onChangeFolding={(folding) => setOpenFolding(folding)}
      />

      {/* 작업 설비 목록 */}
      <SetupDailyReportEquipmentContent
        ref={equipRef}
        readOnly={!isEditable || !hasModifyAuth}
        crtnDt={condition?.crtnDt}
        onChangeWorkers={(result) => {
          // [2026.01.16] 설비 작업자 수 변경 시 변경된 합계 적용되도록 수정
          setPeopleInfo((prev) => {
            return {
              ...prev,
              ctrlCnt: result?.ctrlCnt,
              machCnt: result?.machCnt,
              trspCnt: result?.trspCnt,
              elpwCnt: result?.elpwCnt,
            };
          });
        }}
        onChangeHeavyEquipment={(hepCnt) => {
          setPeopleInfo((prev) => ({ ...prev, hepCnt }));
        }}
      />

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.작업일보 작성', '작업일보 작성')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table} style={{ marginBottom: '24px' }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell style={{ verticalAlign: 'middle' }}>
                <span>{t('setup.label.금일작업내용', '금일작업내용')}</span>
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable && hasModifyAuth ? (
                  <CustomEditorInputWithSearch
                    editorRef={wkProgEditorRef}
                    name="wkProgCtn"
                    value={wkInfo?.wkProgCtn}
                    isError={errors?.wkProgCtn}
                    msgError={String(
                      t('setup.msg.금일작업내용을 입력해 주세요.', '금일작업내용을 입력해 주세요.')
                    )}
                    onLoaded={() => handleEditorOnLoad(wkProgEditorRef, wkInfo?.wkProgCtn)}
                    onKeyActive={(e) => handleEditorChange(e, wkProgEditorRef)}
                    style={{
                      height: '300',
                    }}
                  />
                ) : (
                  <div
                    className="report-read-content"
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(wkInfo?.wkProgCtn, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ verticalAlign: 'middle' }}>
                <span>{t('setup.label.명일작업내용', '명일작업내용')}</span>
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable && hasModifyAuth ? (
                  <CustomEditorInputWithSearch
                    editorRef={nextPlanEditorRef}
                    name="nextPlanCtn"
                    value={wkInfo?.nextPlanCtn}
                    isError={errors?.nextPlanCtn}
                    msgError={String(
                      t('setup.msg.명일작업내용을 입력해 주세요.', '명일작업내용을 입력해 주세요.')
                    )}
                    onLoaded={() => handleEditorOnLoad(nextPlanEditorRef, wkInfo?.nextPlanCtn)}
                    onKeyActive={(e) => handleEditorChange(e, nextPlanEditorRef)}
                    style={{
                      height: '300',
                    }}
                  />
                ) : (
                  <div
                    className="report-read-content"
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(wkInfo?.nextPlanCtn, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ verticalAlign: 'middle' }}>
                <span>{t('setup.label.협력업체 Comment', '협력업체 Comment')}</span>
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable && hasModifyAuth ? (
                  <CustomEditorInputWithSearch
                    editorRef={sngrMatrEditorRef}
                    name="sngrMatrCtn"
                    value={wkInfo?.sngrMatrCtn}
                    isError={errors?.sngrMatrCtn}
                    msgError={String(
                      t(
                        'setup.msg.협력업체 Comment를 입력해 주세요.',
                        '협력업체 Comment를 입력해 주세요.'
                      )
                    )}
                    onLoaded={() => handleEditorOnLoad(sngrMatrEditorRef, wkInfo?.sngrMatrCtn)}
                    onKeyActive={(e) => handleEditorChange(e, sngrMatrEditorRef)}
                    style={{
                      height: '300',
                    }}
                  />
                ) : (
                  <div
                    className="report-read-content"
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(wkInfo?.sngrMatrCtn, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ verticalAlign: 'middle' }}>
                <span>{t('setup.label.LGES Comment', 'LGES Comment')}</span>
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable && hasModifyMgrAuth ? (
                  <CustomTextarea
                    name="excpRelsCtn"
                    placeholder={String(
                      t('setup.msg.LGES Comment를 입력해 주세요.', 'LGES Comment를 입력해 주세요.')
                    )}
                    /*maxLength={1000}*/
                    value={wkInfo?.excpRelsCtn}
                    onChange={(e: any) => {
                      handleWkInfoChange(e);
                    }}
                    readOnly={!isEditable}
                    isError={errors?.excpRelsCtn}
                    msgError={String(
                      t('setup.msg.LGES Comment를 입력해 주세요.', 'LGES Comment를 입력해 주세요.')
                    )}
                    style={{ height: '170px' }}
                  />
                ) : (
                  <div
                    className="report-read-content"
                    dangerouslySetInnerHTML={{
                      __html: CommonUtil.safeHtmlContents(wkInfo?.excpRelsCtn, false),
                    }}
                  ></div>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ verticalAlign: 'middle' }}>
                {t('setup.label.첨부', '첨부')}
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable && hasModifyAuth ? (
                  <Dropzone
                    ref={dropzoneRef}
                    downloadOnly={!editMode}
                    atchFileGrIdInput={wkInfo?.atchFileGrId}
                    atchFileTpCd="SETUP_DAILY"
                  />
                ) : (
                  <FileGrid
                    atchFileGrId={wkInfo?.atchFileGrId ?? ''}
                    atchFileTpCd={'SETUP_DAILY'}
                  />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.일정상세 현황', '일정상세 현황')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <ScheduleStatusDetailContent
        ref={tabStatdRef}
        type={'DAILY_REPORT'}
        isReadOnly={!(isEditable && ensolYn === 'N')}
        showTemplateDownload={isEditable && ensolYn === 'N'}
        showExcelUpload={isEditable && ensolYn === 'N'}
        showReloadResult={isEditable && ensolYn === 'N'}
        onTemplateDownload={downloadExcelTemplate}
        onExcelUploadPreCheck={async () => {
          if (!condition.crtnDt) {
            openCommonModal({
              modalType: 'alert',
              content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
            });
            /*openMessageBar({
              type: 'error',
              content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
            });*/
            return false;
          }

          if (editMode !== PageMode.EDIT) {
            saveDummy();
          }
          return true;
        }}
        onExcelUploadCallback={(file) => {
          openLoading(true);
          uploadExcelUtMatrixTemplates(
            file,
            FileTypeName.TPL_SETUP_DRPT_LIST,
            'NORMAL',
            'TB_EELMB_PRJ_TGT_SP_SCHD_H',
            wkId,
            condition.elmPrjId
          )
            .then((response) => {
              if (response?.data.x_result == 'OK') {
                setExcelValid(true);
                openMessageBar({
                  type: 'confirm',
                  content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
                });

                getStatusContent(wkId);
                // tabStatdRef.current?.search({
                //   elmPrjId: condition.elmPrjId,
                //   excpId: condition.excpId,
                //   wkId: wkId,
                // });
              } else {
                setExcelValidCondition(response?.data.p_xls_upload_id);
                setExcelValidModalOpen(true);
              }
            })
            .finally(() => {
              openLoading(false);
            });
        }}
        onReloadResult={() => {
          // [26.02.02] 실적불러오기, 템플릿다운로드 기준일자 선택 후 클릭 가능으로 변경
          if (!condition.crtnDt) {
            openCommonModal({
              modalType: 'alert',
              content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
            });
            /*openMessageBar({
              type: 'error',
              content: t('com.msg.기준일자를 선택해주세요.', '기준일자를 선택해주세요.'),
            });*/
            return false;
          }
          openCommonModal({
            modalType: 'confirm',
            content: t(
              'setup.label.해당 일자에 등록된 실적을 삭제 후 최신 일자 실적을 불러옵니다.<br/>불러오시겠습니까?',
              '해당 일자에 등록된 실적을 삭제 후 최신 일자 실적을 불러옵니다.<br/>불러오시겠습니까?'
            ),
            yesCallback: async () => {
              openLoading(true);
              reloadScheduleResult(condition.elmPrjId, condition.excpId, wkId, condition.crtnDt)
                .then((res) => {
                  if (res?.successOrNot === 'Y') {
                    getStatusContent(wkId);
                  }
                  openMessageBar({
                    type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
                    content:
                      res?.successOrNot === 'Y'
                        ? t('setup.label.실적을 불러왔습니다.', '실적을 불러왔습니다.')
                        : res.data && typeof res.data === 'string'
                        ? res.data
                        : t(
                            'setup.label.실적불러오기 중 오류가 발생했습니다.',
                            '실적불러오기 중 오류가 발생했습니다.'
                          ),
                  });
                })
                .finally(() => {
                  openLoading(false);
                });
            },
          });
        }}
        onOpenCreateIssuePopup={onOpenCreateIssuePopup}
        crtnDt={condition?.crtnDt || ''}
      />

      <GlobalBtnGroup>
        {renderBtn('CANCEL', 'cancel', t('com.button.취소', '취소') ?? '')}
        {renderBtn('EDIT', 'edit', t('com.button.수정', '수정') ?? '')}
        {renderBtn('DELETE', 'delete', t('com.button.삭제', '삭제') ?? '')}
        {renderBtn('DRAFT', 'draft', t('com.button.임시저장', '임시저장') ?? '')}
        {renderBtn('SAVE', 'save', t('com.button.저장', '저장') ?? '')}
      </GlobalBtnGroup>

      {openIssuePopup && condition?.elmPrjId && condition?.excpId && (
        <ProjectIssueCreatePage
          open={openIssuePopup}
          close={() => {
            setOpenIssuePopup(false);
            getIssueContent(); // 이슈목록 재조회
            getStatusContent(wkInfo?.wkId); // 일정현황 재조회 (이슈 버튼 노출)
          }}
          mode={PageMode.WRITE}
          elmPrjId={condition?.elmPrjId}
          excpId={condition?.excpId}
          prjSpCd={issuePopupCondition?.prjSpCd}
          equip={{
            tgtEqpId: issuePopupCondition?.tgtEqpId,
            eqpId: issuePopupCondition?.eqpId,
            eqpNm: issuePopupCondition?.eqpNm,
            eqclId: issuePopupCondition?.eqclId,
          }}
        />
      )}

      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

export default SetupDailyReportRegistPage;
